import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button } from "theme-ui";
import MobileCard from "components/ui/MobileCard";
import { getDispatchById, getDispatchPin, setDispatchPin } from "api/Projects";
import TextInput from "components/forms/TextInput";
import PasswordInput from "components/forms/PasswordInput";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import BasicButton from "components/forms/BasicButton";
import Page from "./components/Page";
import { trackActivity } from "api/ActivityTracker";
import { useToast } from "components/toast";


const Dispatch = () => {
  const { id } = useParams<{ id: string }>();
  const [dispatch, setDispatch] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pinMatch, setPinMatch] = useState<boolean | null>(null);
  const toast = useToast();

  const fetchDispatch = useCallback(async () => {
    if (!id) {
      setError("Dispatch not found.");
      setDispatch(null);
      return null;
    }

    // The PIN is checked by the server (2026-10-16). Without one (or with a
    // stale one) it returns only the PIN screen's data.
    let res = await getDispatchById({ id });
    if (res?.statusCode === 403 && getDispatchPin(id)) {
      setDispatchPin(id, null);
      res = await getDispatchById({ id });
    }
    if (res?.id) {
      setDispatch(res);
      setPinMatch(res.needsPin ? null : true);
      setError(null);
      return res;
    }

    setError(res?.message || "Dispatch not found.");
    setDispatch(null);
    return null;
  }, [id]);

  useEffect(() => {
    let isMounted = true;

    const loadDispatch = async () => {
      setLoading(true);
      await fetchDispatch();
      if (isMounted) {
        setLoading(false);
      }
    };

    loadDispatch();

    return () => {
      isMounted = false;
    };
  }, [fetchDispatch]);

  const refreshDispatch = useCallback(async () => {
    await fetchDispatch();
  }, [fetchDispatch]);

  const submitPin = async (values: any, formikHelpers: FormikHelpers<any>) => {
    const pin = String(values?.pin ?? "").trim();
    if (!id || !pin) {
      formikHelpers.setSubmitting(false);
      return;
    }
    const res: any = await getDispatchById({ id, pin });
    const matches = !!res?.id && !res?.needsPin;

    void trackActivity({
      module: "dispatches",
      action: "dispatch_login",
      actionType: "auth",
      userId: null,
      companyId: res?.company?.id ?? res?.project?.company?.id ?? null,
      entityType: "dispatch",
      entityId: id,
      metadata: {
        success: matches,
        projectId: res?.project?.id ?? null,
        purchaseOrderId: res?.purchaseOrder?.id ?? null,
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });

    if (matches) {
      setDispatchPin(id, pin);
      setDispatch(res);
      setPinMatch(true);
    } else {
      setPinMatch(false);
      toast.add(res?.statusCode === 403 ? "Incorrect dispatch security pin." : res?.message || "Could not open this dispatch.", "var(--theme-ui-colors-danger)");
    }
    formikHelpers.setSubmitting(false);
  };

  const _validate = Yup.object().shape({
    pin: Yup.string().required("Pin is required."),
  });

  return (
    <MobileCard>
      <Flex sx={{ flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
        {loading ? (
          <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
            <Spinner size={32} strokeWidth={3} title="Loading dispatch" />
          </Flex>
        ) : error ? (
          <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "center", fontFamily: "Rubik", fontSize: "16px", fontWeight: 500 }}>{error}</Flex>
        ) : (
          <>
            {pinMatch !== true ? (
              <Flex sx={{padding: "20px", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%" }}>
                <Flex sx={{ marginTop: "20px", fontFamily: "Rubik", justifyContent: "center", width: "100%", fontSize: "22px", fontWeight: "600" }}>
                  Dispatch# {dispatch?.sequence ? `GMD-${String(dispatch.sequence).padStart(5, "0")}` : ""}
                </Flex>
                <Box sx={{ marginTop: "25px", fontFamily: "Poppins", fontSize: "16px", color: "#4D4D4D", textAlign: "center" }}>Enter the security pin for this dispatch.</Box>
                <Box sx={{ width:"100%"}}>
                  <Formik
                    initialValues={{
                      pin: "",
                    }}
                    onSubmit={submitPin}
                    validationSchema={_validate}
                  >
                    {({ isSubmitting, errors, values, submitCount, setFieldValue, handleSubmit }) => (
                      <Form noValidate autoComplete="off">
                        <PasswordInput
                          name="pin"
                          label="Dispatch Security Pin"
                          value={values.pin || ""}
                          inputMode="numeric"
                          onChange={(val: any) => setFieldValue("pin", val)}
                          $errors={errors.pin && submitCount > 0 ? errors.pin : null}
                          $customStyles={{ flex: 1, marginBottom: "0px", marginTop: "30px", width:"100%" }}
                        />
                        <BasicButton type="submit" $submitting={isSubmitting} styles={{ width: "100%", margin: "10px auto 0px auto", fontSize:"16px", padding:"15px 20px", maxHeight:"40px" }} $loadingColor="#fff" keepText={false}>
                          Submit
                        </BasicButton>
                      </Form>
                    )}
                  </Formik>
                </Box>
              </Flex>
            ) : (
              <Page dispatch={dispatch} onDispatchUpdated={refreshDispatch} />
            )}
          </>
        )}
      </Flex>
    </MobileCard>
  );
};

export default Dispatch;
